import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getKeyboardLangCode, isRTL as isRTLanguage } from '../utils/keyboardUtils';
import OnScreenKeyboard from './OnScreenKeyboard';
import './SentencePracticePage.css';

const DEMO_WORDS = [
  { English: 'to travel', Spanish: 'viajar' },
  { English: 'airport', Spanish: 'el aeropuerto' },
  { English: 'airplane ticket', Spanish: 'el boleto de avión' },
  { English: 'passport', Spanish: 'el pasaporte' },
  { English: 'hotel', Spanish: 'el hotel' },
  { English: 'reservation', Spanish: 'la reservación' },
  { English: 'to check in', Spanish: 'registrarse' },
  { English: 'to check out', Spanish: 'retirarse' },
  { English: 'bus', Spanish: 'el camión' },
  { English: 'taxi', Spanish: 'el taxi' },
  { English: 'subway, metro', Spanish: 'el metro' },
  { English: 'where is...?', Spanish: '¿Dónde está...?' },
  { English: 'how much does it cost?', Spanish: '¿Cuánto cuesta?' },
  { English: 'the bill, the check', Spanish: 'la cuenta' },
  { English: 'please', Spanish: 'por favor' },
  { English: 'thank you', Spanish: 'gracias' },
  { English: 'I am lost', Spanish: 'Estoy perdido/a' },
  { English: 'help', Spanish: '¡Auxilio!, ¡Ayuda!' },
  { English: 'I need a doctor', Spanish: 'Necesito un doctor' },
  { English: 'What\'s up?', Spanish: '¿Qué onda?' },
  { English: 'cool, awesome', Spanish: 'chido' },
  { English: 'No way!, You\'re kidding!', Spanish: '¡No manches!' },
  { English: 'Watch out!, Be careful!', Spanish: '¡Aguas!' },
  { English: 'right on!, go for it!, wow!', Spanish: '¡Órale!' },
  { English: 'dude, guy', Spanish: 'güey' },
];

// Replace DEMO_SENTENCES and DEMO_TRANSLATIONS with new, unique sentences for 10 different words
const DEMO_SENTENCES = [
  'Necesito mi _____ para abordar el avión.', // passport
  'El _____ está en el centro de la ciudad.', // hotel
  '¿Puedes llamar un _____ para mí?', // taxi
  'Reservé una _____ para dos personas.', // reservation
  'Voy a _____ a México este verano.', // to travel
  '¿Dónde está la _____ más cercana?', // estación de metro (use "subway, metro")
  'El _____ sale a las ocho en punto.', // bus
  'Por favor, la _____.', // the bill, the check
  '¿_____?, necesito ayuda.', // help
  'Siempre digo _____ cuando alguien me ayuda.', // thank you
];
const DEMO_TRANSLATIONS = [
  'I need my passport to board the plane.',
  'The hotel is in the city center.',
  'Can you call a taxi for me?',
  'I booked a reservation for two people.',
  'I am going to travel to Mexico this summer.',
  'Where is the nearest subway?',
  'The bus leaves at eight o’clock.',
  'The bill, please.',
  'Help! I need help.',
  'I always say thank you when someone helps me.',
];

// Simple normalization: lowercase, remove accents/diacritics, trim
function normalize(str) {
  return (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[ \-–—]/g, c => c.normalize ? c.normalize('NFD') : c)
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.,/#!$%^&*;:{}=\-_`~()?"'¡¿]/g, '')
    .trim();
}

// Helper to strip leading Spanish articles from a word
function stripSpanishArticle(str) {
  return (str || '').replace(/^(el|la|los|las|un|una|unos|unas)\s+/i, '').trim();
}

const shuffleArray = arr => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

function DemoFIBPracticePage() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [shuffledIndices] = useState(() => shuffleArray([...Array(DEMO_SENTENCES.length).keys()]));
  const [userInputs, setUserInputs] = useState(Array(DEMO_WORDS.length).fill(''));
  const [feedback, setFeedback] = useState(Array(DEMO_WORDS.length).fill(null));
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedInputIdx, setFocusedInputIdx] = useState(null);
  const [keyboardArmedLang, setKeyboardArmedLang] = useState(null);
  const [keyboardActiveIdx, setKeyboardActiveIdx] = useState(null);
  const [showHints, setShowHints] = useState(Array(DEMO_SENTENCES.length).fill(false));
  const inputRefs = useRef([]);

  const handleInputChange = (idx, value) => {
    const newInputs = [...userInputs];
    newInputs[idx] = value;
    setUserInputs(newInputs);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newFeedback = shuffledIndices.map((shufIdx, idx) => {
      const word = [
        DEMO_WORDS[3],  // passport
        DEMO_WORDS[4],  // hotel
        DEMO_WORDS[9],  // taxi
        DEMO_WORDS[5],  // reservation
        DEMO_WORDS[0],  // to travel
        DEMO_WORDS[10], // subway, metro
        DEMO_WORDS[8],  // bus
        DEMO_WORDS[13], // the bill, the check
        DEMO_WORDS[17], // help
        DEMO_WORDS[15], // thank you
      ][shufIdx];
      const correct = normalize(stripSpanishArticle(word.Spanish));
      const user = normalize(stripSpanishArticle(userInputs[idx]));
      if (user === correct) {
        return { status: 'correct', message: t('correct') };
      } else {
        return { status: 'incorrect', message: `${t('incorrect')} ${stripSpanishArticle(word.Spanish)}` };
      }
    });
    setFeedback(newFeedback);
    setIsSubmitted(true);
  };

  const isTargetRTL = isRTLanguage(getKeyboardLangCode('Spanish'));

  return (
    <div className="sentence-practice-container" style={{ marginTop: '3.5rem' }}>
      <button onClick={() => navigate('/module/demo/group/0')} className="btn btn-secondary btn-back" style={{ marginBottom: '2rem' }}>
        {isRTL ? '→' : '←'} {t('backToWordGroup')}
      </button>
      <form onSubmit={handleSubmit}>
        <ol className="sentence-list">
          {shuffledIndices.map((shufIdx, idx) => {
            const sentence = DEMO_SENTENCES[shufIdx];

            const parts = sentence.split('_____');
            const beforeText = parts[0];
            const afterText = parts[1] || '';
            return (
              <li key={idx} className={`sentence-item${isSubmitted && feedback[idx]?.status ? ' answered ' + feedback[idx].status : ''}`}>
                <div className="item-content" style={isTargetRTL ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'center' }}>
                  {/* Show Hint box */}
                  {!showHints[idx] && (
                    <div
                      style={{
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        padding: '0 1.1rem',
                        marginBottom: '1rem',
                        display: 'inline-block',
                        textAlign: 'center',
                        lineHeight: 0.95,
                        transition: 'background 0.18s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f3f3f3'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                    >
                      <button
                        type="button"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#666',
                          fontSize: '0.97rem',
                          padding: 0,
                          margin: 0,
                          fontWeight: 400,
                          cursor: 'pointer',
                          outline: 'none',
                        }}
                        onClick={() => {
                          const newShowHints = [...showHints];
                          newShowHints[idx] = true;
                          setShowHints(newShowHints);
                        }}
                      >
                        {t('showHint')}
                      </button>
                    </div>
                  )}
                  {showHints[idx] && (
                    <p className="translation-hint" style={{ textAlign: 'center' }}>
                      {t('hint')}: "{(() => {
                        const shufIdx = shuffledIndices[idx];
                        return DEMO_TRANSLATIONS[shufIdx];
                      })()}"
                    </p>
                  )}
                  <label className="sentence-text" htmlFor={`fib-input-${idx}`} style={isTargetRTL ? { direction: 'rtl', textAlign: 'right' } : {}}>
                    {idx + 1}. {beforeText}
                    <span className="input-with-keyboard-icon" style={{ display: 'inline-block', position: 'relative', verticalAlign: 'middle' }}>
                      <input
                        id={`fib-input-${idx}`}
                        type="text"
                        value={userInputs[idx]}
                        onChange={e => handleInputChange(idx, e.target.value)}
                        className="inline-sentence-input"
                        disabled={isSubmitted}
                        style={{
                          width: `${Math.max(DEMO_WORDS[idx].English.length, userInputs[idx].length, 5) * 1.1}ch`,
                          direction: isTargetRTL ? 'rtl' : 'ltr',
                          textAlign: 'center',
                          paddingRight: '0.2rem',
                          paddingLeft: '0.2rem',
                        }}
                        ref={el => inputRefs.current[idx] = el}
                        onFocus={() => {
                          setFocusedInputIdx(idx);
                          if (keyboardArmedLang === getKeyboardLangCode('Spanish')) {
                            setKeyboardActiveIdx(idx);
                          }
                        }}
                        onBlur={e => {
                          setFocusedInputIdx(null);
                          setTimeout(() => {
                            const active = document.activeElement;
                            const isKeyboardKey = active && (
                              active.classList.contains('keyboard-key') ||
                              active.closest('.on-screen-keyboard-overlay')
                            );
                            const isSameLang = inputRefs.current.some((input, i) =>
                              input === active && getKeyboardLangCode('Spanish') === keyboardArmedLang
                            );
                            if (!isKeyboardKey && !isSameLang) {
                              setKeyboardActiveIdx(null);
                            }
                          }, 100);
                        }}
                        autoComplete="off"
                      />
                      {focusedInputIdx === idx && (
                        <div style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: '100%',
                          marginTop: '-0.35rem',
                          textAlign: 'center',
                          zIndex: 10
                        }}>
                          <button
                            type="button"
                            className="btn-toggle-keyboard subtle-toggle"
                            tabIndex={-1}
                            style={{
                              fontSize: '1.1rem',
                              background: 'none',
                              border: 'none',
                              color: '#888',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              transition: 'background 0.2s',
                              lineHeight: 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '1.8rem',
                              width: '1.8rem',
                            }}
                            aria-label={t('showKeyboard')}
                            onMouseDown={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              const langCode = getKeyboardLangCode('Spanish');
                              if (keyboardArmedLang === langCode) {
                                setKeyboardArmedLang(null);
                                setKeyboardActiveIdx(null);
                              } else {
                                setKeyboardArmedLang(langCode);
                                setKeyboardActiveIdx(idx);
                                inputRefs.current[idx]?.focus();
                              }
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="5" width="18" height="14" rx="2"/>
                              <line x1="7" y1="15" x2="7" y2="15"/>
                              <line x1="11" y1="15" x2="11" y2="15"/>
                              <line x1="15" y1="15" x2="15" y2="15"/>
                              <line x1="7" y1="11" x2="7" y2="11"/>
                              <line x1="11" y1="11" x2="11" y2="11"/>
                              <line x1="15" y1="11" x2="15" y2="11"/>
                            </svg>
                          </button>
                        </div>
                      )}
                    </span>
                    {afterText}
                  </label>
                  {isSubmitted && feedback[idx] && (
                    <p className={`feedback-text ${feedback[idx].status}`} style={{ direction: 'ltr', textAlign: 'center' }}>
                      {feedback[idx].message}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        <div className="practice-actions">
          {!isSubmitted ? (
            <button type="submit" className="btn btn-primary">
              {t('checkAnswers')}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={() => navigate('/module/demo/group/0')}>
              {t('backToWordGroup')}
            </button>
          )}
        </div>
      </form>
      {keyboardArmedLang && keyboardActiveIdx !== null && (
        <div>
          <OnScreenKeyboard
            languageCode={keyboardArmedLang}
            onInsert={char => {
              const input = inputRefs.current[keyboardActiveIdx];
              if (input) {
                const start = input.selectionStart;
                const end = input.selectionEnd;
                const currentValue = input.value;
                let newValue;
                if (char === 'backspace') {
                  if (start === end) {
                    newValue = currentValue.slice(0, start - 1) + currentValue.slice(end);
                    input.setSelectionRange(start - 1, start - 1);
                  } else {
                    newValue = currentValue.slice(0, start) + currentValue.slice(end);
                    input.setSelectionRange(start, start);
                  }
                } else if (char === 'space') {
                  newValue = currentValue.slice(0, start) + ' ' + currentValue.slice(end);
                  input.setSelectionRange(start + 1, start + 1);
                } else {
                  newValue = currentValue.slice(0, start) + char + currentValue.slice(end);
                  input.setSelectionRange(start + char.length, start + char.length);
                }
                handleInputChange(keyboardActiveIdx, newValue);
                input.focus();
              }
            }}
            onClose={() => {
              setKeyboardArmedLang(null);
              setKeyboardActiveIdx(null);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default DemoFIBPracticePage; 